import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { AlertCircle, Loader2, Receipt } from "lucide-react";
import { Card, CardContent } from "@cms/ui/components/card";
import { Badge } from "@cms/ui/components/badge";
import { Button } from "@cms/ui/components/button";
import { Alert, AlertTitle, AlertDescription } from "@cms/ui/components/alert";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "@cms/ui/components/table";
import { PageHeader } from "@cms/ui/components/page-header";
import { useApiQuery } from "@cms/portal-lib/hooks";
import { formatPence, formatTimestamp } from "@cms/portal-lib/format";
import { invoiceStatusBadgeVariant, openInvoiceHtml } from "@cms/portal-lib/billing";
import { fetchInvoices, invoiceHtmlPath } from "../lib/billing";
import { BillingTabs } from "../components/billing-tabs";
import type { ClientOutletContext } from "../lib/outlet-context";

/** Billing → Invoices: the invoice history table. */
export function BillingInvoicesPage() {
  const { api, setBreadcrumbs } = useOutletContext<ClientOutletContext>();
  const [actionError, setActionError] = useState("");

  useEffect(() => {
    setBreadcrumbs?.([{ label: "Billing", href: "/billing" }, { label: "Invoices" }]);
    return () => setBreadcrumbs?.(null);
  }, [setBreadcrumbs]);

  const invoicesQuery = useApiQuery(() => (api ? fetchInvoices(api) : null), [api]);

  return (
    <div>
      <BillingTabs />

      <PageHeader
        title="Invoices"
        description="Every invoice we've issued — view or print any of them."
      />

      {actionError && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>{actionError}</AlertDescription>
        </Alert>
      )}

      {!invoicesQuery.data && !invoicesQuery.error && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {(invoicesQuery.data || invoicesQuery.error) && (
        <Card>
          <CardContent className="pt-6">
            {invoicesQuery.error && (
              <p className="py-4 text-sm text-destructive">{invoicesQuery.error}</p>
            )}
            {invoicesQuery.data && invoicesQuery.data.invoices.length === 0 && (
              <p className="py-6 text-sm text-muted-foreground">
                No invoices yet — your first one appears after your first payment.
              </p>
            )}
            {invoicesQuery.data && invoicesQuery.data.invoices.length > 0 && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Number</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">View</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoicesQuery.data.invoices.map((invoice) => (
                    <TableRow key={invoice.id}>
                      <TableCell className="font-medium">{invoice.number}</TableCell>
                      <TableCell>{formatTimestamp(invoice.issued_at)}</TableCell>
                      <TableCell>{formatPence(invoice.total_pence)}</TableCell>
                      <TableCell>
                        <Badge variant={invoiceStatusBadgeVariant(invoice.status)}>{invoice.status}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={!api}
                          onClick={() => {
                            if (api) {
                              openInvoiceHtml(api, invoiceHtmlPath(invoice.id))
                                .catch((err: Error) => setActionError(err.message));
                            }
                          }}
                        >
                          <Receipt className="h-3.5 w-3.5" />
                          View / print
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
